<template>
  <section class="fomo-rebill-inline">
    <div v-if="visibleErrorMessage" class="workspace-feedback workspace-feedback--error">{{ visibleErrorMessage }}</div>
    <div v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</div>

    <div v-show="!ready" class="fomo-rebill-inline__loading">
      <span class="fomo-rebill-inline__loading-ring"></span>
      <span>Preparando pago seguro...</span>
    </div>

    <rebill-checkout
      ref="rebillCheckout"
      class="fomo-rebill-inline__checkout"
      :public-key="publicKey"
      :instant-product="instantProductJson"
      :customer-information="customerInformationJson"
      :display="displayConfigJson"
      :css="rebillCss"
      :js="rebillJs"
      :one-click-checkout="false"
      language="es"
    ></rebill-checkout>

    <button class="fomo-rebill-inline__submit" type="button" :disabled="submitting || !ready" @click="submitRebillCheckout">
      {{ submitting ? 'Procesando pago...' : 'Pagar ahora' }}
    </button>
  </section>
</template>

<script>
import 'rebill'

export default {
  name: 'RebillCheckoutElement',
  props: {
    attemptToken: {
      type: String,
      required: true,
    },
    publicKey: {
      type: String,
      required: true,
    },
    instantProduct: {
      type: Object,
      required: true,
    },
    customerInformation: {
      type: Object,
      default: () => ({}),
    },
    amount: {
      type: [Number, String],
      default: 0,
    },
    currency: {
      type: String,
      default: 'USD',
    },
  },
  data() {
    return {
      ready: false,
      errorMessage: '',
      successMessage: '',
      completedEmitted: false,
      removeListeners: [],
      submitting: false,
      submitAttempted: false,
    }
  },
  computed: {
    visibleErrorMessage() {
      return this.submitAttempted ? this.errorMessage : ''
    },
    instantProductJson() {
      return JSON.stringify(this.instantProduct || {})
    },
    customerInformationJson() {
      return JSON.stringify(this.customerInformation || {})
    },
    normalizedCurrency() {
      return String(this.currency || '').trim().toUpperCase()
    },
    isArsCheckout() {
      return this.normalizedCurrency === 'ARS'
    },
    hasCompleteBillingAddress() {
      const address = this.customerInformation && this.customerInformation.address
      const billingAddress = address && address.billingAddress ? address.billingAddress : {}
      const requiresState = this.isArsCheckout
      return Boolean(
        billingAddress.address
        && billingAddress.city
        && billingAddress.country
        && billingAddress.zip
        && (!requiresState || billingAddress.state),
      )
    },
    hasCompleteCustomerInformation() {
      const info = this.customerInformation && typeof this.customerInformation === 'object' ? this.customerInformation : {}
      const hasPhone = Boolean(
        (info.phoneNumber && typeof info.phoneNumber === 'object' && String(info.phoneNumber.number || '').trim())
        || String(info.phoneNumber || '').trim(),
      )
      const hasDocument = !this.isArsCheckout || Boolean(
        String(info.documentType || '').trim() && String(info.documentNumber || '').trim(),
      )
      return Boolean(
        String(info.email || '').trim()
        && String(info.fullName || '').trim()
        && hasPhone
        && hasDocument,
      )
    },
    displayConfigJson() {
      return JSON.stringify({
        checkoutSummary: false,
        submitButton: false,
        billingAddress: !(this.isArsCheckout && this.hasCompleteBillingAddress),
        customerInformation: true,
        discountCode: false,
        logo: false,
        footer: false,
        processingPayment: true,
        successPage: false,
        errorMessage: true,
        useAddressSearch: false,
        excludePaymentMethods: ['cash', 'bank_transfer'],
      })
    },
    rebillJs() {
      return ''
    },
    rebillCss() {
      const arsIdentityCss = this.isArsCheckout
        ? `
user-information,
user-information-static,
rebill-installments {
  display: none !important;
}
`
        : ''
      const pendingSubmitCss = !this.submitAttempted
        ? `
rebill-checkout .error-message,
rebill-checkout .error-message-text,
rebill-checkout [class*="error-message" i],
rebill-checkout [class*="invalid" i],
body:not(:has(rebill-checkout)) .error-message,
body:not(:has(rebill-checkout)) .error-message-text,
body:not(:has(rebill-checkout)) [class*="error-message" i],
body:not(:has(rebill-checkout)) [class*="invalid" i],
body > #root .error-message,
body > #root .error-message-text,
body > #root [class*="error-message" i],
body > #root [class*="invalid" i] {
  display: none !important;
}
`
        : ''
      return `
:root {
  --fomo-rebill-field-bg:
    radial-gradient(circle at 12% 0%, rgba(214, 224, 244, 0.07), transparent 30%),
    radial-gradient(circle at 84% 12%, rgba(116, 88, 168, 0.12), transparent 32%),
    linear-gradient(180deg, rgba(13, 15, 20, 0.88), rgba(7, 8, 12, 0.98));
  --fomo-rebill-popover-bg:
    radial-gradient(circle at 12% 0%, rgba(214, 224, 244, 0.08), transparent 30%),
    radial-gradient(circle at 84% 12%, rgba(116, 88, 168, 0.14), transparent 32%),
    linear-gradient(180deg, rgba(10, 12, 18, 0.98), rgba(6, 7, 11, 0.99));
  --fomo-rebill-field-border: rgba(198, 208, 230, 0.12);
  --fomo-rebill-field-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.055), 0 16px 32px rgba(0, 0, 0, 0.18);
  --fomo-rebill-error: #ff5c7a;
  --fomo-rebill-error-bg: rgba(239, 68, 68, 0.14);
}

rebill-checkout {
  --rebill-color-primary: #eef3fa;
  --rebill-color-primary-hover: #ffffff;
  --rebill-color-primary-dark: #101722;
  --rebill-color-primary-dark-hover: #172333;
  --rebill-color-primary-dark-text: #eef3fa;
  --rebill-color-text-primary: #eef3fa;
  --rebill-color-text-secondary: rgba(216, 224, 239, 0.76);
  --rebill-color-text-secondary-light: rgba(216, 224, 239, 0.48);
  --rebill-color-text-on-primary: #f8fbff;
  --rebill-color-background: transparent;
  --rebill-color-background-left: transparent;
  --rebill-color-background-right: transparent;
  --rebill-color-background-secondary: rgba(255, 255, 255, 0.04);
  --rebill-color-background-secondary-light: rgba(255, 255, 255, 0.06);
  --rebill-color-border: rgba(216, 224, 239, 0.16);
  --rebill-color-border-primary: rgba(216, 224, 239, 0.16);
  --rebill-color-shadow: rgba(0, 0, 0, 0.22);
  --rebill-color-disabled: rgba(216, 224, 239, 0.28);
  --rebill-color-error: var(--fomo-rebill-error);
  --rebill-color-error-background: var(--fomo-rebill-error-bg);
  --input-group-inner-border-radius: 18px;
  display: block;
  width: 100%;
  color: #eef3fa;
  font-family: 'Space Grotesk', Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  box-sizing: border-box;
  overflow: visible;
}

${arsIdentityCss}

${pendingSubmitCss}

rebill-checkout *,
rebill-checkout *::before,
rebill-checkout *::after {
  box-sizing: border-box !important;
}

body:not(:has(rebill-checkout)),
body:not(:has(rebill-checkout)) form,
body:not(:has(rebill-checkout)) main,
body:not(:has(rebill-checkout)) section {
  background: transparent !important;
  color: #eef3fa !important;
  font-family: 'Space Grotesk', Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
}

body:not(:has(rebill-checkout)) form,
body:not(:has(rebill-checkout)) form > div,
body:not(:has(rebill-checkout)) [class*="card" i] {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

body:not(:has(rebill-checkout)) label:has(input[type="checkbox"]),
body:not(:has(rebill-checkout)) input[type="checkbox"],
body:not(:has(rebill-checkout)) [class*="save" i] {
  display: none !important;
}

body:not(:has(rebill-checkout)) label:not(:has(input[type="checkbox"])) {
  display: none !important;
}

body:not(:has(rebill-checkout)) form {
  display: grid !important;
  gap: 12px !important;
  margin: 0 !important;
  padding: 0 !important;
}

body > #root,
body > #root form,
body > #root .section-wrapper {
  background: transparent !important;
  color: #eef3fa !important;
  font-family: 'Space Grotesk', Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
}

body > #root form,
body > #root form > div,
body > #root [class*="card" i] {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

body > #root form {
  display: grid !important;
  gap: 12px !important;
  margin: 0 !important;
  padding: 0 !important;
}

body > #root label:has(input[type="checkbox"]),
body > #root input[type="checkbox"],
body > #root [class*="save" i],
body > #root .top-row,
body > #root .top-row > label,
body > #root .top-row #saveCard {
  display: none !important;
}

body > #root label:not(:has(input[type="checkbox"])) {
  display: none !important;
}

body > #root .section-wrapper {
  gap: 12px !important;
}

body > #root .card-input-container {
  width: 100% !important;
  height: auto !important;
  min-height: 54px !important;
  padding: 0 14px !important;
  border-radius: 18px !important;
  border: 1px solid var(--fomo-rebill-field-border) !important;
  background: var(--fomo-rebill-field-bg) !important;
  background-color: rgba(7, 8, 12, 0.96) !important;
  color: #f5f7fb !important;
  box-shadow: var(--fomo-rebill-field-shadow) !important;
  overflow: hidden !important;
}

body > #root .card-input-container:focus-within {
  border-color: rgba(226, 235, 248, 0.42) !important;
  box-shadow: 0 0 0 3px rgba(93, 185, 177, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.08) !important;
}

body > #root .card-input-container.error-message-border {
  border-color: rgba(239, 68, 68, 0.92) !important;
}

body > #root .error-message,
body > #root .error-message-text,
body > #root [class*="error-message" i],
body > #root [class*="invalid" i],
body:not(:has(rebill-checkout)) .error-message,
body:not(:has(rebill-checkout)) .error-message-text,
body:not(:has(rebill-checkout)) [class*="error-message" i],
body:not(:has(rebill-checkout)) [class*="invalid" i] {
  color: var(--fomo-rebill-error) !important;
  font-weight: 700 !important;
}

body > #root [class*="error" i][role="alert"],
body:not(:has(rebill-checkout)) [class*="error" i][role="alert"] {
  border: 1px solid rgba(239, 68, 68, 0.42) !important;
  background: var(--fomo-rebill-error-bg) !important;
  color: #ffe3ea !important;
  border-radius: 16px !important;
  padding: 10px 12px !important;
}

body > #root .card-input-fields {
  width: 100% !important;
  gap: 10px !important;
  align-items: center !important;
}

body > #root .card-input-icon {
  display: flex !important;
  flex: 0 0 42px !important;
  width: 42px !important;
  min-width: 42px !important;
  max-width: 42px !important;
  height: 34px !important;
  align-items: center !important;
  justify-content: center !important;
  position: relative !important;
  margin-right: 2px !important;
  border-radius: 13px !important;
  border: 1px solid rgba(216, 224, 239, 0.14) !important;
  background:
    radial-gradient(circle at 28% 18%, rgba(238, 243, 250, 0.28), transparent 26%),
    linear-gradient(135deg, rgba(42, 55, 70, 0.94), rgba(13, 18, 28, 0.98)) !important;
  color: rgba(238, 243, 250, 0.9) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 10px 20px rgba(0, 0, 0, 0.22) !important;
  overflow: hidden !important;
}

body > #root .card-input-icon::before {
  content: '' !important;
  width: 22px !important;
  height: 15px !important;
  border-radius: 5px !important;
  border: 1px solid currentColor !important;
  background: linear-gradient(180deg, rgba(238, 243, 250, 0.12), rgba(238, 243, 250, 0.02)) !important;
  opacity: 0.95 !important;
}

body > #root .card-input-icon::after {
  content: '' !important;
  position: absolute !important;
  left: 13px !important;
  top: 15px !important;
  width: 16px !important;
  height: 2px !important;
  border-radius: 999px !important;
  background: currentColor !important;
  opacity: 0.62 !important;
}

body > #root .card-input-icon svg,
body > #root .card-input-icon img,
body > #root .card-input-icon path,
body > #root .cvc-toggle-btn svg,
body > #root .cvc-toggle-btn path {
  color: rgba(216, 224, 239, 0.54) !important;
  stroke: currentColor !important;
}

body > #root .card-input-number {
  flex: 1 1 min(52%, 260px) !important;
  min-width: 130px !important;
}

body > #root .card-input-expiry,
body > #root .card-input-cvc {
  flex: 0 1 84px !important;
  min-width: 58px !important;
}

body > #root .card-input-number,
body > #root .card-input-expiry,
body > #root .card-input-cvc,
body > #root .card-input-holder,
body > #root #card-number,
body > #root #card-expiry,
body > #root #card-cvc,
body > #root #card-holder {
  height: 52px !important;
  min-height: 52px !important;
  border: 0 !important;
  background: transparent !important;
  background-color: transparent !important;
  color: #f5f7fb !important;
  box-shadow: none !important;
  font-family: inherit !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0.01em !important;
  outline: none !important;
}

body > #root #card-number::placeholder,
body > #root #card-expiry::placeholder,
body > #root #card-cvc::placeholder,
body > #root #card-holder::placeholder {
  color: rgba(216, 224, 239, 0.46) !important;
  opacity: 1 !important;
}

body > #root #card-number::placeholder {
  color: rgba(232, 238, 249, 0.58) !important;
  letter-spacing: 0.02em !important;
}

body > #root .error-message {
  color: #ef4444 !important;
  font-size: 0.8rem !important;
  margin-top: 6px !important;
}

body > #root .cvc-toggle-btn {
  display: none !important;
}

body:not(:has(rebill-checkout)) .section-wrapper {
  gap: 12px !important;
}

body:not(:has(rebill-checkout)) .top-row,
body:not(:has(rebill-checkout)) .top-row > label,
body:not(:has(rebill-checkout)) .top-row #saveCard {
  display: none !important;
}

body:not(:has(rebill-checkout)) .card-input-container {
  width: 100% !important;
  height: auto !important;
  min-height: 54px !important;
  padding: 0 14px !important;
  border-radius: 18px !important;
  border: 1px solid var(--fomo-rebill-field-border) !important;
  background: var(--fomo-rebill-field-bg) !important;
  background-color: rgba(7, 8, 12, 0.96) !important;
  color: #f5f7fb !important;
  box-shadow: var(--fomo-rebill-field-shadow) !important;
  overflow: hidden !important;
}

body:not(:has(rebill-checkout)) .card-input-container:focus-within {
  border-color: rgba(226, 235, 248, 0.42) !important;
  box-shadow: 0 0 0 3px rgba(93, 185, 177, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.08) !important;
}

body:not(:has(rebill-checkout)) .card-input-container.error-message-border {
  border-color: rgba(239, 68, 68, 0.92) !important;
}

body:not(:has(rebill-checkout)) .card-input-fields {
  width: 100% !important;
  gap: 10px !important;
  align-items: center !important;
}

body:not(:has(rebill-checkout)) .card-input-icon {
  display: flex !important;
  flex: 0 0 42px !important;
  width: 42px !important;
  min-width: 42px !important;
  max-width: 42px !important;
  height: 34px !important;
  align-items: center !important;
  justify-content: center !important;
  position: relative !important;
  margin-right: 2px !important;
  border-radius: 13px !important;
  border: 1px solid rgba(216, 224, 239, 0.14) !important;
  background:
    radial-gradient(circle at 28% 18%, rgba(238, 243, 250, 0.28), transparent 26%),
    linear-gradient(135deg, rgba(42, 55, 70, 0.94), rgba(13, 18, 28, 0.98)) !important;
  color: rgba(238, 243, 250, 0.9) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 10px 20px rgba(0, 0, 0, 0.22) !important;
  overflow: hidden !important;
}

body:not(:has(rebill-checkout)) .card-input-icon::before {
  content: '' !important;
  width: 22px !important;
  height: 15px !important;
  border-radius: 5px !important;
  border: 1px solid currentColor !important;
  background: linear-gradient(180deg, rgba(238, 243, 250, 0.12), rgba(238, 243, 250, 0.02)) !important;
  opacity: 0.95 !important;
}

body:not(:has(rebill-checkout)) .card-input-icon::after {
  content: '' !important;
  position: absolute !important;
  left: 13px !important;
  top: 15px !important;
  width: 16px !important;
  height: 2px !important;
  border-radius: 999px !important;
  background: currentColor !important;
  opacity: 0.62 !important;
}

body:not(:has(rebill-checkout)) .card-input-icon svg,
body:not(:has(rebill-checkout)) .card-input-icon img,
body:not(:has(rebill-checkout)) .card-input-icon path,
body:not(:has(rebill-checkout)) .cvc-toggle-btn svg,
body:not(:has(rebill-checkout)) .cvc-toggle-btn path {
  color: rgba(216, 224, 239, 0.54) !important;
  stroke: currentColor !important;
}

body:not(:has(rebill-checkout)) .card-input-number {
  flex: 1 1 min(52%, 260px) !important;
  min-width: 130px !important;
}

body:not(:has(rebill-checkout)) .card-input-expiry,
body:not(:has(rebill-checkout)) .card-input-cvc {
  flex: 0 1 84px !important;
  min-width: 58px !important;
}

body:not(:has(rebill-checkout)) .card-input-number,
body:not(:has(rebill-checkout)) .card-input-expiry,
body:not(:has(rebill-checkout)) .card-input-cvc,
body:not(:has(rebill-checkout)) .card-input-holder,
body:not(:has(rebill-checkout)) #card-number,
body:not(:has(rebill-checkout)) #card-expiry,
body:not(:has(rebill-checkout)) #card-cvc,
body:not(:has(rebill-checkout)) #card-holder {
  height: 52px !important;
  min-height: 52px !important;
  border: 0 !important;
  background: transparent !important;
  color: #f5f7fb !important;
  box-shadow: none !important;
  font-family: inherit !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0.01em !important;
  outline: none !important;
}

body:not(:has(rebill-checkout)) #card-number::placeholder,
body:not(:has(rebill-checkout)) #card-expiry::placeholder,
body:not(:has(rebill-checkout)) #card-cvc::placeholder,
body:not(:has(rebill-checkout)) #card-holder::placeholder {
  color: rgba(216, 224, 239, 0.46) !important;
  opacity: 1 !important;
}

body:not(:has(rebill-checkout)) #card-number::placeholder {
  color: rgba(232, 238, 249, 0.58) !important;
  letter-spacing: 0.02em !important;
}

body:not(:has(rebill-checkout)) .error-message {
  color: #ef4444 !important;
  font-size: 0.8rem !important;
  margin-top: 6px !important;
}

body:not(:has(rebill-checkout)) .cvc-toggle-btn {
  display: none !important;
}

body:not(:has(rebill-checkout)) input,
body:not(:has(rebill-checkout)) select,
body:not(:has(rebill-checkout)) textarea,
body:not(:has(rebill-checkout)) .form-control {
  width: 100% !important;
  min-height: 52px !important;
  border-radius: 18px !important;
  border: 1px solid var(--fomo-rebill-field-border) !important;
  background: var(--fomo-rebill-field-bg) !important;
  background-color: rgba(7, 8, 12, 0.96) !important;
  color: #f5f7fb !important;
  box-shadow: var(--fomo-rebill-field-shadow) !important;
  font-family: inherit !important;
  font-size: 15px !important;
  outline: none !important;
  box-sizing: border-box !important;
}

body:not(:has(rebill-checkout)) input:focus,
body:not(:has(rebill-checkout)) select:focus,
body:not(:has(rebill-checkout)) textarea:focus {
  border-color: rgba(226, 235, 248, 0.42) !important;
  box-shadow: 0 0 0 3px rgba(93, 185, 177, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.08) !important;
}

body:not(:has(rebill-checkout)) input::placeholder,
body:not(:has(rebill-checkout)) textarea::placeholder {
  color: rgba(216, 224, 239, 0.46) !important;
}

body:not(:has(rebill-checkout)) label,
body:not(:has(rebill-checkout)) p,
body:not(:has(rebill-checkout)) span {
  color: rgba(232, 238, 249, 0.78) !important;
  font-family: inherit !important;
}

rebill-checkout .rebill-checkout,
rebill-checkout .rebill-checkout.full-width-layout {
  min-height: 0 !important;
  width: 100% !important;
  padding: 0 !important;
  background: transparent !important;
  overflow: visible !important;
}

rebill-checkout .left-section,
rebill-checkout .left-section-container,
rebill-checkout .checkout-form-section,
rebill-checkout .checkout-form-section > div {
  width: 100% !important;
  min-width: 0 !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
}

rebill-checkout .left-section {
  justify-content: stretch !important;
}

rebill-checkout payment-method-selector,
rebill-checkout card-fields-wrapper,
rebill-checkout card-iframe,
rebill-checkout rebill-address,
rebill-checkout rebill-input-select,
rebill-checkout rebill-input-combo,
rebill-checkout rebill-input-phone,
rebill-checkout rebill-installments {
  display: block !important;
  width: 100% !important;
  min-width: 0 !important;
  max-width: none !important;
  position: relative !important;
  overflow: visible !important;
}

${arsIdentityCss}

rebill-checkout .left-section-container,
rebill-checkout .checkout-form-section {
  gap: 14px !important;
}

rebill-checkout organization-logo,
rebill-checkout rebill-footer,
rebill-checkout rebill-mode-sandbox,
rebill-checkout .mode-sandbox-mobile-container,
rebill-checkout .right-section-checkout,
rebill-checkout .summary-mobile-tablet,
rebill-checkout .summary-price-details,
rebill-checkout single-payment-method,
rebill-checkout .payment-button-container rebill-alert {
  display: none !important;
}

rebill-checkout .payment-method-selector > div:first-child {
  display: none !important;
}

rebill-checkout .inputs-container,
rebill-checkout .payment-method-selector,
rebill-checkout card-fields-wrapper > div,
rebill-checkout .animated-component,
rebill-checkout .input-container,
rebill-checkout .input-wrapper,
rebill-checkout .select-wrapper,
rebill-checkout .combo-wrapper,
rebill-checkout .phone-input-container {
  gap: 12px !important;
  overflow: visible !important;
}

rebill-checkout .input-label,
rebill-checkout .input-text__input-label,
rebill-checkout rebill-typography {
  color: rgba(232, 238, 249, 0.76) !important;
  font-family: inherit !important;
}

rebill-checkout .input-element,
rebill-checkout .select-trigger,
rebill-checkout .input-combo-container,
rebill-checkout .combo-wrapper,
rebill-checkout .phone-input-container {
  width: 100% !important;
  height: auto !important;
  min-height: 52px !important;
  border-radius: 18px !important;
  border: 1px solid var(--fomo-rebill-field-border) !important;
  background: var(--fomo-rebill-field-bg) !important;
  background-color: rgba(7, 8, 12, 0.96) !important;
  color: #f5f7fb !important;
  box-shadow: var(--fomo-rebill-field-shadow) !important;
  font-family: inherit !important;
  font-size: 0.95rem !important;
  line-height: 1.25 !important;
}

rebill-checkout .phone-number-input,
rebill-checkout .combo-input,
rebill-checkout .select-combo-trigger {
  min-height: 50px !important;
  color: #f5f7fb !important;
  font-family: inherit !important;
  font-size: 0.95rem !important;
}

rebill-checkout .country-selector,
rebill-checkout .select-section {
  min-height: 50px !important;
  background: transparent !important;
}

rebill-checkout .input-element:focus,
rebill-checkout .select-trigger:focus,
rebill-checkout .combo-wrapper:focus-within,
rebill-checkout .phone-input-container:focus-within {
  border-color: rgba(226, 235, 248, 0.42) !important;
  box-shadow: 0 0 0 3px rgba(93, 185, 177, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.08) !important;
}

rebill-checkout .floating-label .input-element:focus ~ .input-text__input-label,
rebill-checkout .floating-label .input-element:not(:placeholder-shown) ~ .input-text__input-label {
  display: none !important;
}

rebill-checkout .floating-label .input-text__input-label {
  display: none !important;
}

rebill-checkout card-iframe > div,
rebill-checkout card-iframe iframe {
  border-radius: 18px !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  background: transparent !important;
  background-color: transparent !important;
  overflow: visible !important;
}

rebill-checkout card-iframe iframe {
  min-height: 116px !important;
}

rebill-checkout .select-dropPosition.open-down,
rebill-checkout .select-dropPosition.open-up,
rebill-checkout .select-dropdown,
rebill-checkout .country-dropdown {
  z-index: 2147483000 !important;
  max-height: min(260px, 46vh) !important;
  border-radius: 16px !important;
  border: 1px solid var(--fomo-rebill-field-border) !important;
  background: var(--fomo-rebill-popover-bg) !important;
  background-color: rgba(6, 7, 11, 0.99) !important;
  box-shadow: 0 22px 44px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.06) !important;
  color: #eef3fa !important;
  overflow-y: auto !important;
}

rebill-checkout .select-option,
rebill-checkout .country-option,
rebill-checkout .search-container,
rebill-checkout .search-input {
  background: transparent !important;
  color: #eef3fa !important;
  border-color: rgba(216, 224, 239, 0.12) !important;
}

rebill-checkout .select-option:hover,
rebill-checkout .select-option.highlighted,
rebill-checkout .country-option:hover {
  background: rgba(255, 255, 255, 0.08) !important;
}

rebill-checkout .select-option.selected {
  background: rgba(93, 185, 177, 0.22) !important;
  color: #ffffff !important;
}

rebill-checkout .payment-button-container {
  margin: 2px 0 0 !important;
  gap: 0 !important;
}

rebill-checkout .payment-button-container .button.filled.primary-dark {
  min-height: 56px !important;
  border: 0 !important;
  border-radius: 999px !important;
  overflow: hidden !important;
  isolation: isolate !important;
  background: rgba(10, 10, 14, 0.16) !important;
  color: #eef3fa !important;
  box-shadow: 0 18px 34px rgba(2, 4, 10, 0.3), 0 10px 18px rgba(71, 147, 158, 0.12) !important;
  font-family: 'Space Grotesk', Inter, system-ui, sans-serif !important;
  font-weight: 700 !important;
  letter-spacing: -0.02em !important;
}

rebill-checkout .payment-button-container .button.filled.primary-dark::before {
  content: '';
  position: absolute;
  inset: 1px;
  z-index: -1;
  border-radius: inherit;
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.1), transparent 34%),
    linear-gradient(180deg, rgba(24, 39, 48, 0.3), rgba(11, 11, 17, 0.68));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12), inset 0 -16px 24px rgba(93, 185, 177, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.04);
}

rebill-checkout .payment-button-container .button.filled.primary-dark:hover {
  filter: brightness(1.03) !important;
  background: rgba(10, 10, 14, 0.2) !important;
}

rebill-checkout .payment-button-container .button.filled.primary-dark:disabled {
  opacity: 0.56 !important;
}

@media (max-width: 720px) {
  rebill-checkout .rebill-checkout,
  rebill-checkout .rebill-checkout.full-width-layout,
  rebill-checkout .left-section-container,
  rebill-checkout .checkout-form-section {
    width: 100% !important;
    min-width: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  rebill-checkout .payment-button-container .button.filled.primary-dark,
  rebill-checkout .input-element,
  rebill-checkout .select-trigger,
  rebill-checkout .input-combo-container,
  rebill-checkout .combo-wrapper,
  rebill-checkout .phone-input-container {
    min-height: 50px !important;
    border-radius: 16px !important;
  }

  rebill-checkout .select-dropPosition.open-down,
  rebill-checkout .select-dropPosition.open-up,
  rebill-checkout .select-dropdown,
  rebill-checkout .country-dropdown {
    max-height: 42vh !important;
  }

  body:not(:has(rebill-checkout)) form {
    gap: 10px !important;
  }

  body > #root form {
    gap: 10px !important;
  }

  body > #root .card-input-container {
    min-height: 52px !important;
    border-radius: 16px !important;
    padding: 0 10px !important;
    position: relative !important;
  }

  body > #root .card-input-fields {
    gap: 8px !important;
    flex-wrap: nowrap !important;
    align-items: center !important;
  }

  body > #root .card-input-icon {
    order: 1 !important;
    position: relative !important;
    left: auto !important;
    top: auto !important;
    z-index: 1 !important;
    flex: 0 0 36px !important;
    width: 36px !important;
    min-width: 36px !important;
    max-width: 36px !important;
    height: 30px !important;
    border-radius: 12px !important;
    margin-right: 0 !important;
    pointer-events: none !important;
  }

  body > #root .card-input-icon::before {
    width: 20px !important;
    height: 14px !important;
  }

  body > #root .card-input-icon::after {
    left: 12px !important;
    top: 14px !important;
    width: 14px !important;
  }

  body > #root .card-input-number,
  body > #root .card-input-expiry,
  body > #root .card-input-cvc,
  body > #root .card-input-holder,
  body > #root #card-number,
  body > #root #card-expiry,
  body > #root #card-cvc,
  body > #root #card-holder {
    height: 52px !important;
    min-height: 52px !important;
    font-size: 14px !important;
  }

  body > #root .card-input-number {
    order: 2 !important;
    flex: 1 1 auto !important;
    width: auto !important;
    min-width: 122px !important;
    max-width: none !important;
    padding-left: 0 !important;
  }

  body > #root .card-input-holder {
    order: 5 !important;
    flex: 1 1 100% !important;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  body > #root .card-input-expiry {
    order: 3 !important;
  }

  body > #root .card-input-cvc {
    order: 4 !important;
  }

  body > #root .card-input-expiry,
  body > #root .card-input-cvc {
    flex: 0 0 58px !important;
    min-width: 0 !important;
  }

  body:not(:has(rebill-checkout)) .card-input-container {
    min-height: 52px !important;
    border-radius: 16px !important;
    padding: 0 10px !important;
    position: relative !important;
  }

  body:not(:has(rebill-checkout)) .card-input-fields {
    gap: 8px !important;
    flex-wrap: nowrap !important;
    align-items: center !important;
  }

  body:not(:has(rebill-checkout)) .card-input-icon {
    order: 1 !important;
    position: relative !important;
    left: auto !important;
    top: auto !important;
    z-index: 1 !important;
    flex: 0 0 36px !important;
    width: 36px !important;
    min-width: 36px !important;
    max-width: 36px !important;
    height: 30px !important;
    border-radius: 12px !important;
    margin-right: 0 !important;
    pointer-events: none !important;
  }

  body:not(:has(rebill-checkout)) .card-input-icon::before {
    width: 20px !important;
    height: 14px !important;
  }

  body:not(:has(rebill-checkout)) .card-input-icon::after {
    left: 12px !important;
    top: 14px !important;
    width: 14px !important;
  }

  body:not(:has(rebill-checkout)) .card-input-number,
  body:not(:has(rebill-checkout)) .card-input-expiry,
  body:not(:has(rebill-checkout)) .card-input-cvc,
  body:not(:has(rebill-checkout)) .card-input-holder,
  body:not(:has(rebill-checkout)) #card-number,
  body:not(:has(rebill-checkout)) #card-expiry,
  body:not(:has(rebill-checkout)) #card-cvc,
  body:not(:has(rebill-checkout)) #card-holder {
    height: 52px !important;
    min-height: 52px !important;
    font-size: 14px !important;
  }

  body:not(:has(rebill-checkout)) .card-input-number {
    order: 2 !important;
    flex: 1 1 auto !important;
    width: auto !important;
    min-width: 122px !important;
    max-width: none !important;
    padding-left: 0 !important;
  }

  body:not(:has(rebill-checkout)) .card-input-holder {
    order: 5 !important;
    flex: 1 1 100% !important;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }

  body:not(:has(rebill-checkout)) .card-input-expiry {
    order: 3 !important;
  }

  body:not(:has(rebill-checkout)) .card-input-cvc {
    order: 4 !important;
  }

  body:not(:has(rebill-checkout)) .card-input-expiry,
  body:not(:has(rebill-checkout)) .card-input-cvc {
    flex: 0 0 58px !important;
    min-width: 0 !important;
  }

  rebill-checkout .input-combo-container,
  rebill-checkout .phone-input-container {
    align-items: stretch !important;
    max-width: 100% !important;
  }

  rebill-checkout .country-selector {
    flex: 0 0 auto !important;
    max-width: 118px !important;
  }

  rebill-checkout .phone-number-input,
  rebill-checkout .combo-input,
  rebill-checkout .input-element {
    min-width: 0 !important;
  }
}

@media (max-width: 420px) {
  rebill-checkout .payment-button-container .button.filled.primary-dark,
  rebill-checkout .input-element,
  rebill-checkout .select-trigger,
  rebill-checkout .input-combo-container,
  rebill-checkout .combo-wrapper,
  rebill-checkout .phone-input-container {
    min-height: 48px !important;
    border-radius: 14px !important;
    font-size: 0.9rem !important;
  }

  rebill-checkout .country-selector {
    max-width: 106px !important;
  }
}
      `
    },
  },
  mounted() {
    this.bindRebillEvents()
  },
  beforeUnmount() {
    this.removeListeners.forEach((remove) => remove())
    this.removeListeners = []
  },
  methods: {
    extractPaymentId(detail = {}) {
      const data = detail.data && typeof detail.data === 'object' ? detail.data : {}
      const result = data.result && typeof data.result === 'object' ? data.result : {}
      const payment = data.payment && typeof data.payment === 'object' ? data.payment : {}
      const resultPayment = result.payment && typeof result.payment === 'object' ? result.payment : {}
      const candidates = [
        result.paymentId,
        result.payment_id,
        result.id,
        resultPayment.paymentId,
        resultPayment.payment_id,
        resultPayment.id,
        data.paymentId,
        data.payment_id,
        data.id,
        payment.paymentId,
        payment.payment_id,
        payment.id,
        detail.paymentId,
        detail.payment_id,
      ]

      const found = candidates.find((value) => value !== undefined && value !== null && String(value).trim() !== '')
      return found ? String(found).trim() : ''
    },

    extractPaymentStatus(detail = {}) {
      const data = detail.data && typeof detail.data === 'object' ? detail.data : {}
      const result = data.result && typeof data.result === 'object' ? data.result : {}
      return result.status || data.status || detail.status || 'processing'
    },

    detailCode(detail = {}) {
      const data = detail.data && typeof detail.data === 'object' ? detail.data : {}
      const result = data.result && typeof data.result === 'object' ? data.result : {}
      const apiError = data.error && typeof data.error === 'object' ? data.error : {}
      const nestedError = apiError.error && typeof apiError.error === 'object' ? apiError.error : {}
      return String(
        nestedError.type || nestedError.code || apiError.type || apiError.code || result.statusDetail || result.errorType || data.statusDetail || data.code || detail.code || '',
      ).trim()
    },

    emitCompleted(detail = {}) {
      const paymentId = this.extractPaymentId(detail)
      if (!paymentId) {
        return false
      }

      if (this.completedEmitted) {
        return true
      }

      this.completedEmitted = true
      this.successMessage = 'Pago recibido. Estamos validando la aprobacion final.'
      this.$emit('completed', {
        attemptToken: this.attemptToken,
        paymentId,
        paymentMethod: detail.paymentMethod || '',
        status: this.extractPaymentStatus(detail),
      })
      return true
    },

    buildErrorMessage(detail = {}) {
      const data = detail.data && typeof detail.data === 'object' ? detail.data : {}
      const result = data.result && typeof data.result === 'object' ? data.result : {}
      const apiError = data.error && typeof data.error === 'object' ? data.error : {}
      const nestedError = apiError.error && typeof apiError.error === 'object' ? apiError.error : {}
      const message = nestedError.message || apiError.message || result.statusDetail || data.statusDetail || ''
      const code = String(nestedError.type || apiError.type || result.statusDetail || result.errorType || message || '').toLowerCase()

      if (code.includes('invalid_checkout_request')) {
        return message
          ? `Rebill rechazo el pago (${code}): ${message}`
          : `Rebill rechazo el pago (${code}). Si estas usando tarjetas de prueba, confirma que las credenciales ARS sean de una organizacion sandbox.`
      }

      if (code.includes('required_3ds') || code.includes('additional auth')) {
        return 'Tu banco requiere validacion 3DS. Completa la autenticacion y vuelve a intentar si el formulario queda pendiente.'
      }
      if (code.includes('failed_3ds') || code.includes('3ds')) {
        return 'La validacion 3DS no se completo. Intenta nuevamente o usa otra tarjeta.'
      }

      return message || 'No pudimos confirmar el pago. Intenta nuevamente.'
    },

    buildFormErrorMessage(errors = {}) {
      const fields = Object.keys(errors || {})
      if (!fields.length) {
        return ''
      }

      const labels = fields.map((field) => {
        if (field === 'email') return 'correo'
        if (field === 'fullName') return 'nombre y apellido'
        if (field === 'phoneNumber' || field === 'isValidPhoneNumber') return 'telefono'
        if (field === 'documentType' || field === 'documentNumber') return 'DNI/CUIT/CUIL'
        if (field.startsWith('billing-')) return 'direccion de facturacion'
        if (field === 'installments') return 'cuotas'
        return field
      })
      const uniqueLabels = [...new Set(labels)]

      return `Completa los datos requeridos por Rebill: ${uniqueLabels.join(', ')}.`
    },

    showCurrentFormErrors() {
      if (!this.submitAttempted) {
        return
      }

      const element = this.$refs.rebillCheckout
      const checkoutForm = element && element.formElement ? element.formElement : null
      const rebillForm = checkoutForm && checkoutForm.formElement ? checkoutForm.formElement : null
      const errors = rebillForm && rebillForm.errors ? rebillForm.errors : {}
      const message = this.buildFormErrorMessage(errors)
      if (message) {
        this.successMessage = ''
        this.errorMessage = message
        this.submitting = false
      }
    },

    forceSingleInstallment() {
      const element = this.$refs.rebillCheckout
      const checkoutForm = element && element.formElement ? element.formElement : null
      const rebillForm = checkoutForm && checkoutForm.formElement ? checkoutForm.formElement : null
      if (!rebillForm) {
        return
      }

      rebillForm.formData = {
        ...(rebillForm.formData || {}),
        installments: '1',
      }
      if (rebillForm.errors && rebillForm.errors.installments) {
        rebillForm.errors = {
          ...rebillForm.errors,
          installments: '',
        }
      }
    },

    async submitRebillCheckout() {
      const element = this.$refs.rebillCheckout
      this.submitAttempted = true
      if (!element || typeof element.submit !== 'function') {
        this.errorMessage = 'Rebill todavia no termino de cargar el formulario. Intenta de nuevo en un segundo.'
        return
      }

      this.errorMessage = ''
      this.successMessage = ''
      this.submitting = true
      this.forceSingleInstallment()
      try {
        await element.submit()
      } catch (error) {
        this.errorMessage = (error && error.message) || 'Rebill no pudo enviar el formulario.'
        this.submitting = false
        return
      }

      window.setTimeout(() => this.showCurrentFormErrors(), 100)
      window.setTimeout(() => this.showCurrentFormErrors(), 600)
      window.setTimeout(() => {
        if (this.submitting && !this.successMessage && !this.errorMessage) {
          this.submitting = false
        }
      }, 3000)
    },

    bindRebillEvents() {
      const element = this.$refs.rebillCheckout
      if (!element) return

      const bind = (eventName, handler) => {
        element.addEventListener(eventName, handler)
        this.removeListeners.push(() => element.removeEventListener(eventName, handler))
      }

      bind('ready', () => {
        this.ready = true
      })

      bind('success', (event) => {
        this.submitting = false
        const detail = event && event.detail ? event.detail : {}
        if (!this.emitCompleted(detail)) {
          this.errorMessage = 'El procesador aprobo el formulario, pero no devolvio paymentId.'
        }
      })

      bind('successRedirect', (event) => {
        this.submitting = false
        const detail = event && event.detail ? event.detail : {}
        this.successMessage = 'Redirigiendo validacion segura 3DS...'
        this.emitCompleted(detail)
      })

      bind('error', (event) => {
        this.submitting = false
        const detail = event && event.detail ? event.detail : {}
        this.errorMessage = this.buildErrorMessage(detail)
      })

      bind('formChange', (event) => {
        const detail = event && event.detail ? event.detail : {}
        const message = this.buildFormErrorMessage(detail.errors || {})
        if (message && this.submitAttempted) {
          this.successMessage = ''
          this.errorMessage = message
        } else if (this.errorMessage && this.errorMessage.startsWith('Completa los datos requeridos')) {
          this.errorMessage = ''
        }
      })

      const markRejected = (event) => {
        const detail = event && event.detail ? event.detail : {}
        const code = this.detailCode(detail)
        this.submitting = false
        this.successMessage = ''
        this.errorMessage = this.buildErrorMessage(detail)
        if (code) {
          this.errorMessage = `${code}: ${this.errorMessage}`
        }
      }

      const markPending = () => {
        this.submitting = false
        this.errorMessage = ''
        this.successMessage = 'Pago pendiente. Estamos esperando la confirmacion de Rebill.'
      }

      ;['failed', 'failure', 'rejected', 'paymentFailed', 'paymentRejected', 'declined'].forEach((eventName) => {
        bind(eventName, markRejected)
      })
      ;['pending', 'processing', 'paymentPending', 'paymentProcessing'].forEach((eventName) => {
        bind(eventName, markPending)
      })
    },
  },
}
</script>

<style scoped>
.fomo-rebill-inline {
  display: block;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background: transparent;
  overflow: visible;
}

.fomo-rebill-inline__loading {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  margin: 2px 0 14px;
  color: rgba(232, 238, 249, 0.76);
  font-size: 0.88rem;
  font-weight: 600;
}

.fomo-rebill-inline__loading-ring {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.18);
  border-top-color: #f7fafc;
  animation: fomoRebillSpin 0.9s linear infinite;
}

.fomo-rebill-inline__checkout {
  display: block;
  width: 100%;
}

.fomo-rebill-inline__submit {
  width: 100%;
  min-height: 56px;
  margin-top: 12px;
  border: 1px solid rgba(216, 224, 239, 0.18);
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(31, 41, 48, 0.92), rgba(14, 22, 28, 0.98));
  color: #f7fafc;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  cursor: pointer;
  font-size: 0.98rem;
  font-weight: 800;
}

.fomo-rebill-inline__submit:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

@keyframes fomoRebillSpin {
  to {
    transform: rotate(360deg);
  }
}
</style>
